import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack, StepBack, StepForward, SkipForward, X, Clock, Loader2 } from "lucide-react";
import { useReplay } from "../../context/ReplayContext";
import { ReplayEngine } from "../../engine";

const fmtDT = (epoch) => {
  if (!epoch) return "—";
  const d = new Date(epoch * 1000);
  return `${d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" })} • ${d.toLocaleTimeString([], { hour12: false })}`;
};
const fmtN = (n) => n.toLocaleString();

// Transport bar for a replay session. Drives the ReplayEngine only — the chart just sees ticks.
export default function ReplayBar({ tf }) {
  const { replay, engine } = useReplay();
  const [scrub, setScrub] = useState(null);
  const sliderRef = useRef(null);
  const range = Math.max(1, replay.endTime - replay.startTime);
  const pos = ((scrub ?? Math.max(replay.cursor, replay.startTime)) - replay.startTime) / range;
  const tfSec = tf.kind === "tick" ? null : tf.seconds;
  const tfTicks = tf.kind === "tick" ? tf.ticks : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "SELECT" || e.target.tagName === "TEXTAREA") return;
      if (e.code === "Space") { e.preventDefault(); replay.playing ? engine.pause() : engine.play(); }
      if (e.key === "ArrowRight") { e.preventDefault(); e.shiftKey ? engine.stepCandle(tfSec, tfTicks) : engine.stepTick(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); engine.stepBack(tfSec, tfTicks); }
      if (e.key === "Escape") engine.stop();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [replay.playing, engine, tfSec, tfTicks]);

  const commitScrub = () => { if (scrub != null) { engine.seek(scrub); setScrub(null); } };

  return (
    <div data-testid="replay-bar" className="shrink-0 border-t border-primary/40 bg-primary/5 px-3 py-2 flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold tracking-widest text-primary px-1.5 py-0.5 rounded bg-primary/15" data-testid="replay-badge">REPLAY</span>
        <div className="flex items-center gap-0.5 ml-1">
          <Btn testid="replay-restart" title="Restart" onClick={() => engine.restart()}><SkipBack className="h-3.5 w-3.5" /></Btn>
          <Btn testid="replay-step-back" title="Back one candle (←)" onClick={() => engine.stepBack(tfSec, tfTicks)}><StepBack className="h-3.5 w-3.5" /></Btn>
          <Btn testid="replay-play" title={replay.playing ? "Pause (space)" : "Play (space)"} primary onClick={() => (replay.playing ? engine.pause() : engine.play())} disabled={replay.loading}>
            {replay.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : replay.playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Btn>
          <Btn testid="replay-step-tick" title="Step one tick (→)" onClick={() => engine.stepTick()}><StepForward className="h-3.5 w-3.5" /></Btn>
          <Btn testid="replay-step-candle" title="Step one candle (shift+→)" onClick={() => engine.stepCandle(tfSec, tfTicks)}><SkipForward className="h-3.5 w-3.5" /></Btn>
        </div>
        <div className="flex items-center gap-0.5 ml-2" data-testid="replay-speeds">
          {ReplayEngine.SPEEDS.map((s) => (
            <button key={s} data-testid={`replay-speed-${s}`} onClick={() => engine.setSpeed(s)}
              className={`px-1.5 h-6 rounded text-[11px] font-mono-nums ${replay.speed === s ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:bg-secondary"}`}>
              {s}×
            </button>
          ))}
        </div>
        <div className="ml-3 flex items-center gap-1.5 text-xs font-mono-nums" data-testid="replay-cursor">
          <Clock className="h-3.5 w-3.5 text-primary" /> {fmtDT(scrub ?? replay.cursor)}
        </div>
        <div className="ml-auto text-[11px] text-muted-foreground font-mono-nums" data-testid="replay-progress">
          {replay.loading ? "Loading history…" : replay.finished ? "Reached live edge" : `Ticks ${fmtN(replay.emitted)} / ~${fmtN(range)}`}
          {replay.error && <span className="text-sell ml-2">{replay.error}</span>}
        </div>
        <button data-testid="replay-exit" onClick={() => engine.stop()} title="Exit replay (esc)" className="ml-2 h-7 px-2 rounded-lg border border-border text-xs flex items-center gap-1 hover:bg-secondary">
          <X className="h-3.5 w-3.5" /> Exit
        </button>
      </div>
      <input ref={sliderRef} data-testid="replay-timeline" type="range" min={replay.startTime} max={replay.endTime} step={1}
        value={scrub ?? Math.max(replay.cursor, replay.startTime)}
        onChange={(e) => setScrub(+e.target.value)} onMouseUp={commitScrub} onTouchEnd={commitScrub} onKeyUp={commitScrub}
        className="replay-slider w-full" style={{ "--pos": `${Math.min(100, Math.max(0, pos * 100))}%` }} />
    </div>
  );
}

function Btn({ children, onClick, title, testid, primary, disabled }) {
  return (
    <button data-testid={testid} title={title} onClick={onClick} disabled={disabled}
      className={`h-7 w-7 rounded-lg flex items-center justify-center disabled:opacity-50 ${primary ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
      {children}
    </button>
  );
}
